/**
 * optional-products.js
 *
 * - Adds main product + selected optional stand-alone products to cart in one request
 * - Triggers cart drawer refresh/open
 * - Handles optional description "read more" only when text overflows 1 line
 * - Handles "view all / view less" for extra optional items
 */

(() => {
  let busy = false;

  const SELECTOR_FORMS = 'form[action*="/cart/add"]';
  const SELECTOR_OPTIONAL_CHECKED = '.js-optional:checked';
  const OPTIONAL_ROOT_SELECTOR = '.product-optionals';
  const OPTIONAL_DESC_SELECTOR = '.product-optionals__desc';
  const OPTIONAL_DESC_TEXT_SELECTOR = '.product-optionals__desc-text';
  const OPTIONAL_DESC_TOGGLE_SELECTOR = '.product-optionals__desc-toggle';
  const OPTIONAL_TOGGLE_SELECTOR = '.js-optionals-toggle, .product-optionals__toggle';
  const OPTIONAL_EXTRA_SELECTOR = '[data-optional-extra="1"]';

  let descRaf = null;
  let resizeTimer = null;
  let lastTouchTs = 0;

  function getCheckedOptionalVariantIds() {
    return Array.from(document.querySelectorAll(SELECTOR_OPTIONAL_CHECKED))
      .map((el) => Number(el.value))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  function getMainVariantIdFromForm(form) {
    const idInput = form.querySelector('input[name="id"]');
    const id = Number(idInput?.value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function getQtyFromForm(form) {
    const qtyInput = form.querySelector('input[name="quantity"]');
    const q = Number(qtyInput?.value || 1);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  function getDrawerSectionIds() {
    const drawer = document.querySelector('cart-drawer-component');

    const raw =
      drawer?.getAttribute('sections') ||
      drawer?.dataset?.sections ||
      '';

    if (raw) {
      return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    return ['cart-drawer', 'cart-icon-bubble'];
  }

  async function cartAdd(items) {
    const sections = getDrawerSectionIds();

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const json = await res.json().catch(() => ({}));

    if (!res.ok) {
      console.error('cart/add.js failed', res.status, json);
      throw new Error(`cart_add_failed_${res.status}`);
    }

    return json;
  }

  function dispatchCartUpdate(cartResponse, sourceId = 'optional-products') {
    const ev = new Event('cart:update', { bubbles: true });

    ev.detail = {
      resource: cartResponse,
      sourceId,
      data: {
        source: 'optional-products',
        didError: false,
        itemCount: cartResponse?.item_count,
        sections: cartResponse?.sections,
      },
    };

    document.dispatchEvent(ev);
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    document.dispatchEvent(new Event('cart:open', { bubbles: true }));
  }

  function isElementVisible(el) {
    if (!el) return false;
    if (el.hidden) return false;
    if (el.closest('[hidden]')) return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function getLineHeightPx(el) {
    if (!el) return 0;
    const style = window.getComputedStyle(el);
    const raw = style.lineHeight;

    if (raw && raw.endsWith('px')) {
      const val = parseFloat(raw);
      if (Number.isFinite(val)) return val;
    }

    const fontSize = parseFloat(style.fontSize || '16');
    if (Number.isFinite(fontSize)) return fontSize * 1.2;

    return 19.2;
  }

  function resetOptionalDesc(desc, toggle) {
    if (!desc) return;

    desc.classList.remove('has-more', 'is-expanded');

    if (toggle) {
      toggle.style.display = 'none';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = toggle.dataset.more || toggle.textContent || '';
    }
  }

  function updateOptionalDescription(desc) {
    if (!desc) return;

    const text = desc.querySelector(OPTIONAL_DESC_TEXT_SELECTOR);
    const toggle = desc.querySelector(OPTIONAL_DESC_TOGGLE_SELECTOR);

    if (!text || !toggle) return;

    resetOptionalDesc(desc, toggle);

    if (!isElementVisible(text)) return;

    const prevClamp = text.style.webkitLineClamp;
    const prevDisplay = text.style.display;
    const prevOverflow = text.style.overflow;

    text.style.webkitLineClamp = '1';
    text.style.display = '-webkit-box';
    text.style.overflow = 'hidden';

    const lineHeight = getLineHeightPx(text);
    const maxAllowed = Math.ceil(lineHeight + 1);
    const isOverflowing = text.scrollHeight > maxAllowed + 1;

    if (isOverflowing) {
      desc.classList.add('has-more');
      toggle.style.display = 'inline-block';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = toggle.dataset.more || toggle.textContent || '';
    }

    text.style.webkitLineClamp = prevClamp;
    text.style.display = prevDisplay;
    text.style.overflow = prevOverflow;
  }

  function scheduleOptionalDescriptionsUpdate(root = document) {
    if (descRaf) cancelAnimationFrame(descRaf);

    descRaf = requestAnimationFrame(() => {
      root.querySelectorAll(OPTIONAL_DESC_SELECTOR).forEach(updateOptionalDescription);
      descRaf = null;
    });
  }

  async function handleOptionalAdd(form, submitBtn) {
    const optionalIds = getCheckedOptionalVariantIds();
    if (!optionalIds.length) return false;

    const mainVariantId = getMainVariantIdFromForm(form);
    if (!mainVariantId) return false;

    if (busy) return true;
    busy = true;

    if (submitBtn) submitBtn.disabled = true;

    try {
      const items = [
        { id: mainVariantId, quantity: getQtyFromForm(form) },
        ...optionalIds.map((id) => ({ id, quantity: 1 })),
      ];

      const cartResponse = await cartAdd(items);
      dispatchCartUpdate(cartResponse);
      openCartDrawer();

      return true;
    } catch (err) {
      console.error('Optional add failed:', err);
      alert('Errore durante l’aggiunta degli optional. Riprova.');
      return true;
    } finally {
      busy = false;
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  function bindForm(form) {
    if (form.dataset.optionalsBound === '1') return;
    form.dataset.optionalsBound = '1';

    form.addEventListener('submit', async (e) => {
      const submitBtn = form.querySelector('[type="submit"]');
      const handled = await handleOptionalAdd(form, submitBtn);
      if (handled) e.preventDefault();
    });

    form.querySelectorAll('[type="submit"], button[name="add"]').forEach((btn) => {
      btn.addEventListener(
        'click',
        async (e) => {
          const handled = await handleOptionalAdd(form, btn);
          if (handled) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    });
  }

  function handleDescToggleClick(e) {
    const btn = e.target.closest?.(OPTIONAL_DESC_TOGGLE_SELECTOR);
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const desc = btn.closest(OPTIONAL_DESC_SELECTOR);
    if (!desc || !desc.classList.contains('has-more')) return;

    const expanded = desc.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    btn.textContent = expanded
      ? (btn.dataset.less || 'Riduci')
      : (btn.dataset.more || 'Leggi di più');
  }

  function setOpen(wrap, open) {
    wrap.classList.toggle('is-open', open);

    const toggle = wrap.querySelector(OPTIONAL_TOGGLE_SELECTOR);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    wrap.querySelectorAll(OPTIONAL_EXTRA_SELECTOR).forEach((el) => {
      el.hidden = !open;
    });

    setTimeout(() => scheduleOptionalDescriptionsUpdate(wrap), 40);
    setTimeout(() => scheduleOptionalDescriptionsUpdate(wrap), 160);
  }

  function bindOptionalRoot(wrap) {
    if (!wrap || wrap.dataset.optionalsToggleBound === '1') return;
    wrap.dataset.optionalsToggleBound = '1';

    const toggle = wrap.querySelector(OPTIONAL_TOGGLE_SELECTOR);
    if (!toggle) {
      scheduleOptionalDescriptionsUpdate(wrap);
      return;
    }

    const hasExtras = !!wrap.querySelector(OPTIONAL_EXTRA_SELECTOR);
    if (!hasExtras) {
      toggle.style.display = 'none';
    } else {
      setOpen(wrap, false);

      if (!toggle.matches('a,button,input,summary') && !toggle.hasAttribute('tabindex')) {
        toggle.setAttribute('tabindex', '0');
        toggle.setAttribute('role', toggle.getAttribute('role') || 'button');
      }
    }

    scheduleOptionalDescriptionsUpdate(wrap);
  }

  function initOptionals(root = document) {
    root.querySelectorAll(OPTIONAL_ROOT_SELECTOR).forEach(bindOptionalRoot);
  }

  function handleOptionalToggleEvent(e) {
    const toggle = e.target.closest?.(OPTIONAL_TOGGLE_SELECTOR);
    if (!toggle) return;

    const wrap = toggle.closest?.(OPTIONAL_ROOT_SELECTOR);
    if (!wrap) return;

    const now = Date.now();
    if (e.type === 'click' && now - lastTouchTs < 650) {
      e.preventDefault();
      return;
    }
    if (e.type === 'touchend') lastTouchTs = now;

    e.preventDefault();
    e.stopPropagation();

    const open = wrap.classList.contains('is-open');
    setOpen(wrap, !open);
  }

  function init() {
    document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
    initOptionals(document);
    scheduleOptionalDescriptionsUpdate(document);
  }

  document.addEventListener('DOMContentLoaded', init);

  document.addEventListener('shopify:section:load', (e) => {
    document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
    initOptionals(e.target || document);
    scheduleOptionalDescriptionsUpdate(e.target || document);
  });

  document.addEventListener('variant:update', () => {
    document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
    scheduleOptionalDescriptionsUpdate(document);
  });

  document.addEventListener('click', handleDescToggleClick, true);
  document.addEventListener('touchend', handleOptionalToggleEvent, true);
  document.addEventListener('click', handleOptionalToggleEvent, true);

  document.addEventListener(
    'keydown',
    (e) => {
      const toggle = e.target.closest?.(OPTIONAL_TOGGLE_SELECTOR);
      if (!toggle) return;

      const wrap = toggle.closest?.(OPTIONAL_ROOT_SELECTOR);
      if (!wrap) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle.click();
      }
    },
    true
  );

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => scheduleOptionalDescriptionsUpdate(document), 120);
  });

  window.addEventListener('load', () => {
    scheduleOptionalDescriptionsUpdate(document);
    setTimeout(() => scheduleOptionalDescriptionsUpdate(document), 180);
    setTimeout(() => scheduleOptionalDescriptionsUpdate(document), 600);
  });
})();